import { HUD_DISPLAY_FY } from "@/data/manual-facts";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Suspense } from "react";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { PageOutro } from "@/components/site/PageEssentials";
import { Section, CTABand, FAQ } from "@/components/site/Blocks";
import { getStateHero } from "@/data/state-heroes";
import { incomeLimitFaqs } from "@/lib/hud-data-faqs";
import { getStateIncomeLimits, type IlRow } from "@/lib/hud-il.functions";
import { getState } from "@/data/states";
import { buildHead } from "@/lib/seo-head";
import { GovernmentAffiliationNotice } from "@/components/site/GovernmentAffiliationNotice";
import { LocalMarketNews } from "@/components/site/LocalMarketNews";
import { getMarketStats } from "@/lib/market-stats.functions";

const ilOptions = (state: string) =>
  queryOptions({
    queryKey: ["stateIl", state],
    queryFn: () => getStateIncomeLimits({ data: { state } }),
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });

export const Route = createFileRoute("/hud-income-limits/$state")({
  loader: async ({ params, context }) => {
    const state = getState(params.state);
    if (!state) throw notFound();
    // fire-and-forget — the component uses suspense query and handles errors
    context.queryClient.ensureQueryData(ilOptions(state.abbrev)).catch(() => {});
    const marketStats = await getMarketStats({ data: { stateSlug: state.slug } }).catch(() => null);
    return { state, marketStats };
  },
  head: ({ params, loaderData }) => {
    const state = loaderData?.state;
    if (!state) return { meta: [{ title: "HUD Income Limits by State | Simply Approved Mortgages" }] };
    const url = `/hud-income-limits/${params.state}`;
    const hero = getStateHero(state.slug);
    const title = `Income Limits in ${state.name} | Simply Approved`;
    const description = `HUD Section 8 Income Limits by county in ${state.name}. 30% / 50% / 80% AMI thresholds for households of 1–8 — live from the HUD User API.`;
    return buildHead({
      title,
      description,
      url,
      type: "article",
      keywords: `${state.name} HUD income limits, ${state.name} Section 8 income limits, ${state.name} AMI thresholds, ${state.name} LIHTC income limits, ${state.name} area median income, ${state.name} extremely low income limit, ${state.name} very low income limit, ${state.name} low income limit, HUD income limits ${state.name} 2026, ${state.name} Section 8 eligibility income`,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "HUD Income Limits", url: "/hud-income-limits" },
        { name: state.name, url },
      ],
      service: { name: "HUD Income Limits Lookup", state: state.name },
      faqs: incomeLimitFaqs(state.name),
      image: hero?.src,
      imageAlt: hero?.alt ?? `HUD Income Limits data for ${state.name}`,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: `${HUD_DISPLAY_FY} HUD Income Limits — ${state.name}`,
          description,
          creator: { "@type": "GovernmentOrganization", name: "U.S. Department of Housing and Urban Development" },
          license: "https://www.huduser.gov/portal/datasets/il.html",
          spatialCoverage: { "@type": "AdministrativeArea", name: state.name },
          temporalCoverage: "2026",
        },
      ],
    });
  },
  component: IlStatePage,
  notFoundComponent: () => (
    <SiteShell>
      <Section>
        <p>
          State not found.{" "}
          <Link to="/hud-income-limits" className="text-brand underline">
            See all states
          </Link>
          .
        </p>
      </Section>
    </SiteShell>
  ),
  errorComponent: ({ error }) => (
    <SiteShell>
      <Section>
        <div className="mx-auto max-w-3xl rounded-xl border border-border bg-card p-6">
          <h1 className="font-display text-xl font-semibold text-foreground">HUD Income Limits temporarily unavailable</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            HUD's live Income Limits API didn't respond for this request. This usually clears within a minute — try
            refreshing. Detail: {(error as Error).message}
          </p>
          <p className="mt-3 text-sm">
            <Link to="/hud-fair-market-rents" className="text-brand underline">
              Browse Fair Market Rents instead →
            </Link>
          </p>
        </div>
      </Section>
    </SiteShell>
  ),
});

function IlStatePage() {
  const { state, marketStats } = Route.useLoaderData();

  return (
    <SiteShell>
      <PageHero
        eyebrow="HUD Income Limits"
        title={`${HUD_DISPLAY_FY} HUD Income Limits in ${state.name}`}
        subtitle={`Every county's 30% / 50% / 80% AMI thresholds for FY${HUD_DISPLAY_FY} — Section 8 eligibility and LIHTC compliance data from HUD's published tables.`}
        primaryLabel="Start My Approval"
        primaryHref={`/section-8-loans/${state.slug}`}
        secondaryLabel="See Today's Rates"
        secondaryHref={`/hud-fair-market-rents/${state.slug}`}
      />
      {/* The hero renders outside Suspense so the <h1> is present in the SSR
          HTML even while the live HUD table is still streaming in. */}
      <Suspense fallback={<TableSkeleton />}>
        <IlData state={state} />
      </Suspense>
      <Section>
        <div className="mx-auto max-w-3xl">
          <h2 className="mb-4 text-center font-display text-2xl font-bold text-foreground">
            {state.name} HUD Income Limits — FAQs
          </h2>
          <FAQ items={incomeLimitFaqs(state.name)} />
        </div>
      </Section>
      <LocalMarketNews
        stateName={state.name}
        stateAbbrev={state.abbrev}
        stats={marketStats ?? null}
        statsScope="state"
      />
      <div className="container mx-auto max-w-7xl px-4 pb-10">
        <GovernmentAffiliationNotice />
      </div>

      <CTABand title={`Financing a Section 8 or LIHTC property in ${state.name}?`} body={`We DSCR-underwrite HAP contracts and LIHTC-compliant rents in Florida, Colorado, and other states where LLC business-purpose loans are permitted.`} />
      <PageOutro />
    </SiteShell>
  );
}

function TableSkeleton() {
  return (
    <Section>
      <div className="mx-auto max-w-5xl animate-pulse space-y-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-20 rounded-xl border border-border bg-card" />
          ))}
        </div>
        <div className="h-64 rounded-xl border border-border bg-card" />
      </div>
    </Section>
  );
}

function IlData({ state }: { state: { name: string; abbrev: string; slug: string } }) {
  const { data } = useSuspenseQuery(ilOptions(state.abbrev));
  const rows = [...(data.entries ?? [])].sort((a, b) => b.medianIncome - a.medianIncome);
  const medians = rows.map((r) => r.medianIncome).filter((v) => v > 0);
  const median = medians.length ? medians.slice().sort((a, b) => a - b)[Math.floor(medians.length / 2)] : 0;
  const max = medians.length ? Math.max(...medians) : 0;
  const min = medians.length ? Math.min(...medians) : 0;

  if (rows.length === 0) {
    return (
      <Section>
        <div className="mx-auto max-w-3xl rounded-xl border border-border bg-card p-6">
          <h2 className="font-display text-xl font-semibold text-foreground">
            {state.name} income limits are temporarily unavailable
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            HUD's Income Limits service isn't responding right now, so we're not showing figures rather than showing
            numbers we can't verify. Please check back shortly, or view the official tables at{" "}
            <a
              href="https://www.huduser.gov/portal/datasets/il.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand underline"
            >
              HUD User
            </a>
            .
          </p>
          <div className="mt-4 flex flex-wrap gap-3 text-sm">
            <Link to="/hud-fair-market-rents/$state" params={{ state: state.slug }} className="rounded-md border border-border px-4 py-2 hover:border-brand">
              Fair Market Rents in {state.name}
            </Link>
            <Link to="/section-8-loans/$state" params={{ state: state.slug }} className="rounded-md bg-brand px-4 py-2 font-semibold text-primary-foreground hover:bg-brand/90">
              Section 8 DSCR loans in {state.name}
            </Link>
          </div>
        </div>
      </Section>
    );
  }

  return (
    <Section>
      <div className="mx-auto max-w-5xl">
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-brand-gold/30 bg-brand-gold/5 px-4 py-2 text-xs">
          <span className="rounded-full bg-brand px-2 py-0.5 font-semibold uppercase tracking-wide text-primary-foreground">HUD FY{data.year}</span>
          <span className="text-muted-foreground">
            Source:{" "}
            <a href={data.sourceUrl ?? "https://www.huduser.gov/portal/datasets/il.html"} target="_blank" rel="noopener noreferrer" className="underline">
              HUD User — FY{data.year} Section 8 Income Limits
            </a>
            {data.retrievedAt
              ? ` · retrieved from HUD ${new Date(data.retrievedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "America/New_York" })}`
              : ""}
            . HUD publishes new limits once a year (typically spring); figures stay as published until HUD releases the next fiscal year.
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="Counties / areas" value={rows.length.toLocaleString()} />
          <Stat label="Highest AMI" value={`$${max.toLocaleString()}`} />
          <Stat label="Median AMI" value={`$${median.toLocaleString()}`} />
          <Stat label="Lowest AMI" value={`$${min.toLocaleString()}`} />
        </div>

        <h2 className="mt-12 font-display text-2xl font-bold text-foreground">
          Top 10 highest-AMI counties in {state.name}
        </h2>
        <IlTable rows={rows.slice(0, 10)} />

        <h2 className="mt-12 font-display text-2xl font-bold text-foreground">
          10 lowest-AMI counties in {state.name}
        </h2>
        <IlTable rows={rows.slice(-10).reverse()} />

        <div className="mt-12 rounded-xl border border-border bg-card p-6">
          <h3 className="font-display text-xl font-semibold text-foreground">What are HUD Income Limits?</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            HUD publishes Income Limits annually to determine eligibility for Section 8, public housing, and LIHTC
            programs. The three tiers are Extremely Low (30% AMI), Very Low (50% AMI), and Low Income (80% AMI). LIHTC
            investors in {state.name} use the 50% and 60% AMI thresholds to set max rents on qualifying units.
          </p>
        </div>

        <div className="mt-8 flex flex-wrap gap-3 text-sm">
          <Link to="/hud-income-limits" className="rounded-md border border-border px-4 py-2 hover:border-brand">All states →</Link>
          <Link to="/hud-fair-market-rents/$state" params={{ state: state.slug }} className="rounded-md border border-border px-4 py-2 hover:border-brand">
            Fair Market Rents in {state.name}
          </Link>
          <Link to="/section-8-loans/$state" params={{ state: state.slug }} className="rounded-md bg-brand px-4 py-2 font-semibold text-primary-foreground hover:bg-brand/90">
            Section 8 DSCR loans in {state.name}
          </Link>
        </div>
      </div>
    </Section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-xl font-semibold text-foreground">{value}</div>
    </div>
  );
}

function IlTable({ rows }: { rows: IlRow[] }) {
  if (rows.length === 0) return <p className="mt-4 text-sm text-muted-foreground">No rows.</p>;
  return (
    <div className="mt-4 overflow-x-auto rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-left">
          <tr>
            <th className="px-3 py-2 font-medium">County / area</th>
            <th className="px-3 py-2 text-right font-medium">Median income</th>
            <th className="px-3 py-2 text-right font-medium">ELI (30%) 4-person</th>
            <th className="px-3 py-2 text-right font-medium">VLI (50%) 4-person</th>
            <th className="px-3 py-2 text-right font-medium">LIL (80%) 4-person</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.fipsCode + r.countyName} className="border-t border-border">
              <td className="px-3 py-2">{r.countyName}</td>
              <td className="px-3 py-2 text-right">${r.medianIncome.toLocaleString()}</td>
              <td className="px-3 py-2 text-right">${(r.eli[3] ?? 0).toLocaleString()}</td>
              <td className="px-3 py-2 text-right">${(r.vli[3] ?? 0).toLocaleString()}</td>
              <td className="px-3 py-2 text-right">${(r.lil[3] ?? 0).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
